import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  agentToolsApi,
  type CreateMCPConnectionPayload,
  type CreateMCPConnectionResponse,
} from "../../../services/agent-tools-service";
import { MCP_CONNECTIONS_QUERY_KEY } from "./useMCPConnections";

type ApiError = {
  response?: {
    data?: { error?: { message?: string }; detail?: string; message?: string };
  };
  message?: string;
};

const extractErrorMessage = (
  error: ApiError,
  fallback: string,
  duplicateConnectionMessage: string,
  language: string,
): string => {
  const data = error.response?.data;
  const message =
    data?.error?.message || data?.detail || data?.message || error.message;

  if (/MCP connection with this endpoint already exists/i.test(message ?? "")) {
    return duplicateConnectionMessage;
  }

  return language.toLowerCase().startsWith("ar")
    ? fallback
    : message || fallback;
};

type Options = {
  onSuccess?: (response: CreateMCPConnectionResponse) => void;
};

export const useCreateMCPConnection = (options: Options = {}) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateMCPConnectionPayload) =>
      agentToolsApi.createMCPConnection(payload),
    onSuccess: (response) => {
      // OAuth toast/redirect is handled by the page so it can synchronously
      // open the new tab from the form-submit click (popup blockers reject
      // window.open calls scheduled after async work).
      if (!response.oauth_init) {
        toast.success(t("mcpPage.mutation.added"));
      }
      queryClient.invalidateQueries({ queryKey: MCP_CONNECTIONS_QUERY_KEY });
      options.onSuccess?.(response);
    },
    onError: (error: ApiError) => {
      toast.error(
        extractErrorMessage(
          error,
          t("mcpPage.mutation.addFailed"),
          t("mcpPage.errors.connectionExists"),
          i18n.resolvedLanguage ?? i18n.language,
        ),
      );
    },
  });
};
